import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { orderBy } from 'natural-orderby';
import queryString from 'query-string';
import { getDataPromise, putDataPromise } from 'brock-fetch-utils';
import {
    PERSISTED_LINK,
    EXTERNAL_LINK,
    decompressEncodedURLToConfiguration,
    DASHBOARD_LINK,
    noLinksExist,
    multipleLinksExist,
    SAVED_DASHBOARD_NAME,
} from '../dashboardLinkUtils';
import {
    getDashboardStateFromQueryParams,
    getKeyFromSavedDashboardName,
    setFiltersBasedOnLanguage,
} from '../dashboardUtils';
import { sanitizeTimeAndPrecision } from '../utils/configurationAndValidationUtils';
import {
    parseUserPreferenceValue,
    getUserPreferenceKey,
} from '../../shared/userPreferenceUtils';
import { useLabelContext } from '../../shared/hooks/labelContext';

function getSavedStateForPersistence(key, stateToPersist) {
    return {
        key,
        value: JSON.stringify(stateToPersist),
    };
}

function addKeyAndNameToSavedDashboard(savedDashboard) {
    return {
        ...savedDashboard.value,
        key: savedDashboard.key,
        name: savedDashboard.name,
    };
}

function appendDefaultSavedDashboard(savedDashboards, dashboardLabels) {
    const defaultSavedDashboard = {
        name: dashboardLabels.default,
        key: 'Default',
    };

    return [defaultSavedDashboard, ...savedDashboards];
}

function getSharedUrlById(uriConstants, id) {
    return getDataPromise(uriConstants.getSharedUrl(id)).then(
        (response) => response.payload
    );
}

async function getSharedUrlResponse(queryParams, uriConstants) {
    let sharedUrlResponse;

    if (queryParams[PERSISTED_LINK]) {
        sharedUrlResponse = await getSharedUrlById(
            uriConstants,
            queryParams[PERSISTED_LINK]
        );
    } else if (queryParams[EXTERNAL_LINK]) {
        sharedUrlResponse = queryParams[EXTERNAL_LINK];
    } else {
        sharedUrlResponse = queryParams[DASHBOARD_LINK];
    }

    return sharedUrlResponse;
}

/* eslint-disable max-params, max-statements, complexity */
async function appendExternalLinkConfiguration(
    initialConfiguration,
    savedDashboards,
    queryParams,
    labels,
    uriConstants,
    selectOptions
) {
    let hydratedConfiguration;

    try {
        if (
            isEmpty(queryParams) ||
            multipleLinksExist(queryParams) ||
            queryParams[SAVED_DASHBOARD_NAME]
        ) {
            return savedDashboards;
        }

        if (!isEmpty(queryParams) && noLinksExist(queryParams)) {
            const {
                urlTimeAndPrecision,
                urlFilterValues,
                urlWhereClauses,
                urlRecordLimit,
            } = getDashboardStateFromQueryParams(
                queryParams,
                initialConfiguration.availableFilters,
                initialConfiguration.whereClauseOptions,
                selectOptions,
                labels.predicateLabels
            );

            hydratedConfiguration = {
                timeAndPrecision: {
                    ...initialConfiguration.timeAndPrecision,
                    ...urlTimeAndPrecision,
                },
                filterValues: {
                    ...initialConfiguration.filterValues,
                    ...urlFilterValues,
                },
                whereClauses: [
                    ...initialConfiguration.whereClauses,
                    ...urlWhereClauses,
                ],
                recordLimit:
                    urlRecordLimit.recordLimit ||
                    initialConfiguration.recordLimit,
                isDescending:
                    !!urlRecordLimit.isDescending ||
                    !!initialConfiguration.isDescending,
            };
        } else {
            const sharedUrlResponse = await getSharedUrlResponse(
                queryParams,
                uriConstants
            );
            if (!sharedUrlResponse) {
                return savedDashboards;
            }

            hydratedConfiguration = await decompressEncodedURLToConfiguration(
                sharedUrlResponse
            );

            setFiltersBasedOnLanguage(
                hydratedConfiguration,
                selectOptions,
                initialConfiguration.availableFilters,
                labels.predicateLabels
            );

            if (queryParams[DASHBOARD_LINK]) {
                hydratedConfiguration = {
                    timeAndPrecision: {
                        ...initialConfiguration.timeAndPrecision,
                        ...hydratedConfiguration.timeAndPrecision,
                    },
                    filterValues: {
                        ...initialConfiguration.filterValues,
                        ...hydratedConfiguration.filterValues,
                    },
                    whereClauses: [
                        ...initialConfiguration.whereClauses,
                        ...hydratedConfiguration.whereClauses,
                    ],
                };
            }
        }

        const externalLinkDashboard = {
            name: labels.dashboardLabels.sharedDashboard,
            key: '',
            ...hydratedConfiguration,
        };

        return [externalLinkDashboard, ...savedDashboards];
    } catch (error) {
        /* eslint-disable no-alert, prefer-destructuring */
        alert(labels.dashboardLabels.externalLinkErrorMessage);
        window.location.href = window.location.href.split('?')[0];
        throw error;
        /* eslint-enable */
    }
}
/* eslint-enable */

export default function ExternalState(props) {
    const labels = useLabelContext();

    const [isLoaded, setIsLoaded] = useState(false);
    const [savedDashboards, setSavedDashboards] = useState([]);
    const [loadedSavedDashboard, setLoadedDashboard] = useState({});
    const [savedDashboardError, setSavedDashboardError] = useState(null);
    const [userPreferences, setUserPreferences] = useState({
        savedDashboards: [],
    });

    const setLoadedSavedDashboard = useCallback((configuration) => {
        setLoadedDashboard({
            ...configuration,
            timeAndPrecision: sanitizeTimeAndPrecision(
                configuration.timeAndPrecision,
                configuration.predefinedDateRanges,
                configuration.precisionTypeOptions
            ),
        });
    }, []);

    const onGetSavedDashboardError = useCallback(() => {
        const defaultDashboards = appendDefaultSavedDashboard(
            [],
            labels.dashboardLabels
        );

        setSavedDashboardError(
            labels.dashboardLabels.errorRetrievingSavedDashboards
        );
        setSavedDashboards(defaultDashboards);
        setLoadedSavedDashboard({
            ...props.initialConfiguration,
            ...defaultDashboards[0],
        });
        setIsLoaded(true);
    }, [
        setLoadedSavedDashboard,
        labels.dashboardLabels,
        props.initialConfiguration,
    ]);

    /* eslint-disable complexity */
    useEffect(
        () => {
            if (props.initialStateLoaded) {
                getDataPromise(
                    props.uriConstants.getUserPreferences(
                        getUserPreferenceKey(
                            props.dashboardKey,
                            props.initialConfiguration.viewKey
                        )
                    )
                ).then(async (response) => {
                    const parsedPreferences =
                        parseUserPreferenceValue(response);

                    const parsedSavedDashboards = orderBy(
                        parsedPreferences.savedDashboards.map(
                            addKeyAndNameToSavedDashboard
                        ),
                        ['name']
                    );

                    const savedDashboardsWithDefault =
                        appendDefaultSavedDashboard(
                            parsedSavedDashboards,
                            labels.dashboardLabels
                        );

                    const queryParams = queryString.parse(
                        props.location.search
                    );
                    const savedDashboardsWithExternalLink =
                        await appendExternalLinkConfiguration(
                            props.initialConfiguration,
                            savedDashboardsWithDefault,
                            queryParams,
                            labels,
                            props.uriConstants,
                            props.selectOptions
                        );

                    let dashboardToLoad = savedDashboardsWithExternalLink[0];

                    const savedDashboardName =
                        queryParams[SAVED_DASHBOARD_NAME] ??
                        props.initialConfiguration.key;

                    if (savedDashboardName) {
                        const savedDashboard =
                            savedDashboardsWithExternalLink.find(
                                (x) => x.key === savedDashboardName
                            );

                        if (savedDashboard) {
                            dashboardToLoad = savedDashboard;
                        }
                    }

                    setSavedDashboards(savedDashboardsWithExternalLink);
                    setLoadedSavedDashboard({
                        ...props.initialConfiguration,
                        ...dashboardToLoad,
                    });
                    setIsLoaded(true);
                    setSavedDashboardError(null);
                    setUserPreferences(parsedPreferences);
                }, onGetSavedDashboardError);
            }
        },
        /* eslint-disable react-hooks/exhaustive-deps */
        [
            props.dashboardKey,
            props.initialStateLoaded,
            props.uriConstants,
            props.location,
            labels,
            props.initialConfiguration,
            props.selectOptions,
            onGetSavedDashboardError,
        ]
        /* eslint-enable react-hooks/exhaustive-deps */
    );

    const saveDashboard = (name, isSaveNew, stateToPersist) => {
        const userPreferenceKey = getUserPreferenceKey(
            props.dashboardKey,
            props.initialConfiguration.viewKey
        );

        // prettier-ignore
        const key = isSaveNew
            ? `${getUserPreferenceKey(
                props.dashboardKey,
                props.initialConfiguration.viewKey
            )}_${getKeyFromSavedDashboardName(name)}`
            : name;

        /* eslint-disable indent */
        const dashboardName = isSaveNew
            ? name
            : savedDashboards.find(
                  (savedDashboard) => savedDashboard.key === name
              ).name;
        /* eslint-enable indent */

        let preferences;
        if (isSaveNew) {
            preferences = {
                ...userPreferences,
                savedDashboards: [
                    ...userPreferences.savedDashboards,
                    {
                        key,
                        name: dashboardName,
                        value: stateToPersist,
                    },
                ],
            };
        } else {
            const savedDashboardToUpdate = userPreferences.savedDashboards.find(
                (x) => x.key === key
            );

            const otherSavedDashboards = userPreferences.savedDashboards.filter(
                (x) => x.key !== key
            );

            preferences = {
                ...userPreferences,
                savedDashboards: [
                    ...otherSavedDashboards,
                    {
                        ...savedDashboardToUpdate,
                        value: stateToPersist,
                    },
                ],
            };
        }

        putDataPromise(
            props.uriConstants.saveUserPreferences,
            getSavedStateForPersistence(userPreferenceKey, preferences)
        ).then(
            () =>
                getDataPromise(
                    props.uriConstants.getUserPreferences(
                        getUserPreferenceKey(
                            props.dashboardKey,
                            props.initialConfiguration.viewKey
                        )
                    )
                ).then((response) => {
                    const parsedPreferences =
                        parseUserPreferenceValue(response);

                    const savedDashboardsWithDefault =
                        appendDefaultSavedDashboard(
                            parsedPreferences.savedDashboards.map(
                                addKeyAndNameToSavedDashboard
                            ),
                            labels.dashboardLabels
                        );

                    const savedDashboardToLoad =
                        savedDashboardsWithDefault.find(
                            (savedDashboard) => savedDashboard.key === key
                        );

                    setSavedDashboards(savedDashboardsWithDefault);
                    setLoadedSavedDashboard({
                        ...props.initialConfiguration,
                        ...savedDashboardToLoad,
                    });
                    setSavedDashboardError(null);
                    setUserPreferences(parsedPreferences);
                }, onGetSavedDashboardError),
            () => {
                setSavedDashboardError(
                    labels.dashboardLabels.errorSavingDashboard
                );
            }
        );
    };

    const deleteSavedDashboard = (key) => {
        const userPreferenceKey = getUserPreferenceKey(
            props.dashboardKey,
            props.initialConfiguration.viewKey
        );

        const preferences = {
            ...userPreferences,
            savedDashboards: userPreferences.savedDashboards.filter(
                (x) => x.key !== key
            ),
        };

        putDataPromise(
            props.uriConstants.saveUserPreferences,
            getSavedStateForPersistence(userPreferenceKey, preferences)
        ).then(
            () =>
                getDataPromise(
                    props.uriConstants.getUserPreferences(
                        getUserPreferenceKey(
                            props.dashboardKey,
                            props.initialConfiguration.viewKey
                        )
                    )
                ).then(
                    (response) => {
                        const parsedPreferences =
                            parseUserPreferenceValue(response);

                        const savedDashboardsWithDefault =
                            appendDefaultSavedDashboard(
                                parsedPreferences.savedDashboards.map(
                                    addKeyAndNameToSavedDashboard
                                ),
                                labels.dashboardLabels
                            );

                        setUserPreferences(parsedPreferences);
                        setSavedDashboards(savedDashboardsWithDefault);
                        setSavedDashboardError(null);

                        if (key === loadedSavedDashboard.key) {
                            const savedDashboardToLoad =
                                savedDashboardsWithDefault.find(
                                    (savedDashboard) =>
                                        savedDashboard.key === 'Default'
                                );

                            setLoadedSavedDashboard({
                                ...props.initialConfiguration,
                                ...savedDashboardToLoad,
                            });
                        }
                    },
                    () => onGetSavedDashboardError()
                ),
            () => {
                setSavedDashboardError(
                    labels.dashboardLabels.errorDeletingSavedDashboard
                );
            }
        );
    };

    /* eslint-disable complexity */
    const loadSavedDashboard = (key) => {
        const savedDashboardToLoad = savedDashboards.find(
            (savedDashboard) => savedDashboard.key === key
        );

        setFiltersBasedOnLanguage(
            savedDashboardToLoad,
            props.selectOptions,
            props.initialConfiguration.availableFilters,
            labels.predicateLabels
        );

        const defaultFilterValues =
            key === 'Default' ? { filterValues: {} } : {};

        setLoadedSavedDashboard({
            ...props.initialConfiguration,
            ...defaultFilterValues,
            ...savedDashboardToLoad,
        });
    };
    /* eslint-enable */

    return props.render({
        deleteSavedDashboard,
        isLoaded: props.initialStateLoaded && isLoaded,
        loadedSavedDashboard,
        loadSavedDashboard,
        saveDashboard,
        savedDashboardError,
        savedDashboardNames: savedDashboards.map((savedDashboard) => ({
            name: savedDashboard.name,
            key: savedDashboard.key,
        })),
    });
}

ExternalState.propTypes = {
    dashboardKey: PropTypes.string.isRequired,
    initialConfiguration: PropTypes.instanceOf(Object),
    userPreferences: PropTypes.instanceOf(Object),
    initialStateLoaded: PropTypes.bool.isRequired,
    labels: PropTypes.instanceOf(Object),
    location: PropTypes.instanceOf(Object),
    render: PropTypes.func.isRequired,
    selectOptions: PropTypes.instanceOf(Array).isRequired,
    uriConstants: PropTypes.instanceOf(Object).isRequired,
};

ExternalState.defaultProps = {
    initialConfiguration: {},
    location: {},
};
